import type {ReactElement} from 'react';
import React from 'react';
import {getCurrentPageHashedID, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {BottomBarItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/bottom-bar-item-color-fill';
import {getUrlForImageItemBigTierScreenSize} from '@Libraries/image-item.library';
import {repoVideoThumbImageURL} from '@Libraries/user-video-library';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {getAssetUrl} from '@Utils/s3.util';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {useAppSelector} from '@/hooks';
import styles from './background-item-fill-item.module.scss';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';

export function BackgroundItemFillItem(): ReactElement<any> | null {
  const currentPageHashedID = useAppSelector(getCurrentPageHashedID);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  if (!currentBackgroundObject || !currentPageHashedID) {
    return null;
  }

  const currentPage = window.posterEditor?.whiteboard?.pages.getPage(currentPageHashedID);

  if (!currentPage) {
    return null;
  }

  const getImg = (src: string): ReactElement<any> => {
    return <img className={`${styles.img} radius-4 border-s-standard`} src={src} alt={window.i18next.t('pmwjs_image_preview')} />;
  };

  const getFillItem = (): ReactElement<any> => {
    switch (currentBackgroundObject.details.type) {
      case BackgroundTypeName.COLOR:
        if (currentBackgroundObject.details.fill.fillType === FillTypes.NONE) {
          return getImg(getAssetUrl(`poster-editor-thumbnails/transparent.${getCompatibleImageFileExtension('webp')}`));
        }
        return <BottomBarItemColorFill type={currentBackgroundObject.details.fill.fillType} color={currentBackgroundObject.details.fill.fillColor} />;
      case BackgroundTypeName.IMAGE:
        if (currentBackgroundObject.details.hashedFilename && currentBackgroundObject.details.imageSource) {
          return getImg(
            getUrlForImageItemBigTierScreenSize(
              currentBackgroundObject.details.hashedFilename,
              currentBackgroundObject.details.imageSource
            )
          );
        }
        if (currentBackgroundObject.details.uploadingImageData?.dataUrl) {
          return getImg(currentBackgroundObject.details.uploadingImageData.dataUrl);
        }
        throw new Error('Unable to get image source for image background');
      case BackgroundTypeName.VIDEO: {
        const {hashedFilename, fileExtension} = currentBackgroundObject.details;
        if (hashedFilename && fileExtension) {
          return getImg(repoVideoThumbImageURL(hashedFilename, fileExtension));
        }
        throw new Error('Unable to get thumbnail source for video background');
      }
      default:
        throw new Error(`Unhandled backgroundDropDownItem`);
    }
  };

  return getFillItem();
}
